import { z } from 'zod'

/**
 * 결제 원장 (C51, 2026-10-27 대표 결정). 결제사 웹훅의 결제·환불 이벤트 1건 = 원장 1행((결제사, 이벤트 id) 가 유일 키).
 * 상태 목록은 개발지시서 132행 그대로다. 지금 웹훅으로 쓰는 값은 paid·refunded·partially_refunded 셋이고,
 * 나머지는 스토어 밖 결제(나중)를 위해 자리만 둔다
 */
export const PAYMENT_STATUSES = [
  'pending',
  'paid',
  'failed',
  'cancelled',
  'partially_refunded',
  'refunded',
] as const
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number]

/**
 * 원장 결제사 (payment_ledger.provider). 이벤트 id 는 결제사 안에서만 유일해서 유일 키가 (결제사, 이벤트 id) 다
 * (2026-10-30 대표 Top10 #4). revenuecat = 앱스토어·플레이 구매(RevenueCat 웹훅), stripe = 샘플 거래, dev_fake = 개발용 가짜 결제
 */
export const PAYMENT_PROVIDERS = ['revenuecat', 'stripe', 'dev_fake'] as const
export type PaymentProvider = (typeof PAYMENT_PROVIDERS)[number]

/**
 * 환불된 소모품(견적권·생성 팩)을 어떻게 했는지.
 * revoked = 안 쓴 견적권(또는 안 쓴 팩)을 잔액에서 뺐다 · partly_used = 팩의 남은 횟수만 뺐고 쓴 몫은 확인 대상 ·
 * already_used = 이미 견적에 쓴 견적권이라 그대로 두고 확인 대상 · not_found = 지급 행을 못 찾음 ·
 * partial_refund = 부분 환불이라 빼지 않고 확인 대상 · revoke_off = 관리자 스위치(refundRevoke)가 꺼져 빼지 않음 ·
 * refund_reversed = 환불이 취소됨(뺀 것을 자동으로 돌려주지 않고 확인 대상)
 * amount_unverified = 원결제·환불 금액·통화·환경을 확인할 수 없어 자동 회수하지 않음 ·
 * payment_unrecorded = 지급 행은 찾았지만 원장에 결제 행이 없음(원장 도입 전 결제 등). 결제 금액을 몰라 부분 환불과
 * 구별할 수 없어 자동 회수하지 않고, 운영자가 스토어에서 전액 환불을 확인한 뒤 수동으로 회수한다 ·
 * used_before_refund = 사용 후 환불. 구독(Pro)이 환불됐는데 그 기간의 포함 견적권을 이미 견적에 썼다. 안 쓴 포함분은 뺐고,
 * 쓴 몫은 견적을 그대로 두고 확인 대상(2026-10-29 대표 결정)
 */
export const PAYMENT_GRANT_ACTIONS = [
  'revoked',
  'partly_used',
  'already_used',
  'not_found',
  'partial_refund',
  'revoke_off',
  'refund_reversed',
  'amount_unverified',
  'payment_unrecorded',
  'used_before_refund',
] as const
export type PaymentGrantAction = (typeof PAYMENT_GRANT_ACTIONS)[number]

/** GET /admin/users/:id/payments 의 원장 1행 */
export interface AdminPaymentLedgerView {
  id: string
  /** 결제사. 옛 서버는 보내지 않는다 */
  provider?: string
  eventId: string
  storeTransactionId: string | null
  grantTransactionId: string | null
  eventType: string
  status: PaymentStatus
  productId: string | null
  store: string | null
  isSandbox: boolean
  /** 결제 금액(결제 통화). 원래 결제를 모르는 환불 행은 null */
  amount: number | null
  /** 이번 이벤트로 환불된 금액(결제 통화, 양수) */
  refundAmount: number | null
  currency: string | null
  grantAction: PaymentGrantAction | null
  /** 운영자 확인 대상 — 자동으로 회수하지 않은 환불 */
  needsReview: boolean
  createdAt: string
  /** 관리자가 확인 처리한 기록(작업 기록에서 읽음). 옛 서버는 보내지 않는다 */
  review?: AdminPaymentReviewRecord | null
}

/**
 * 환불이 가리키는 지급 행의 종류. quote = 견적권 구매, included = Pro 구독 포함 견적권(그 구독 거래 id 로 잇는다),
 * pack = 생성 팩, review = 전문가 검수권 구매(견적권과 같은 quote_credits 표, 상품 id 로 구분)
 */
export type PaymentGrantKind = 'quote' | 'included' | 'pack' | 'review'

/** 확인 대상 행의 관리자 처리. revoke = 안 쓴 지급분 회수, dismiss = 회수하지 않고 확인 완료 */
export const PAYMENT_REVIEW_DECISIONS = ['revoke', 'dismiss'] as const
export type PaymentReviewDecision = (typeof PAYMENT_REVIEW_DECISIONS)[number]
/** 처리 기록의 admin_audit.action (target = payment / 원장 행 id). 관리자 작업 기록 화면도 이 값으로 이름을 붙인다 */
export const PAYMENT_REVIEW_AUDIT_ACTIONS = {
  revoke: 'payment.review.revoke',
  dismiss: 'payment.review.dismiss',
} as const satisfies Record<PaymentReviewDecision, string>

/** POST /admin/users/:id/payments/:paymentId/review — 사유는 작업 기록에 남는다 */
export const adminPaymentReviewSchema = z.object({
  decision: z.enum(PAYMENT_REVIEW_DECISIONS),
  reason: z.string().trim().min(1).max(200),
})
export type AdminPaymentReview = z.infer<typeof adminPaymentReviewSchema>

/** 확인 처리 기록 1건 (admin_audit) */
export interface AdminPaymentReviewRecord {
  decision: PaymentReviewDecision
  adminEmail: string
  reason: string
  at: string
}

/** 확인 대상 행이 가리키는 지급 행 1개 */
export interface AdminPaymentGrantView {
  transactionId: string
  kind: PaymentGrantKind
  productId: string
  purchasedAt: string
  /** 이 견적권을 쓴 견적 id. 쓴 견적권은 회수하지 않는다 */
  quoteId: string | null
  /** 이 검수권을 쓴 전문가 검수 id. 쓴 검수권은 회수하지 않는다. 옛 서버는 보내지 않는다 */
  expertReviewId?: string | null
  /** 팩만: 쓴 횟수와 전체 횟수 */
  used: number | null
  size: number | null
  /** 이미 적힌 환불 원장 이벤트 id */
  refundEventId: string | null
  /** 지금 관리자 회수로 뺄 수 있는지 (안 썼고 환불 표시가 없음) */
  revocable: boolean
}

/** GET /admin/users/:id/payments/:paymentId/review */
export interface AdminPaymentReviewTarget {
  grants: AdminPaymentGrantView[]
  /** 회수 버튼을 쓸 수 있는지: 확인 대상 · 전액 환불 · 자동 회수가 돌지 않은 처리 · 뺄 지급분이 있음 */
  canRevoke: boolean
}
